import { useMyPoints, usePublicGamificationSettings } from '@/hooks';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { PixelAvatar } from './PixelAvatar';
import { cn } from '@/lib/utils';
import { Star } from 'lucide-react';

interface GamifiedAvatarProps {
  src?: string | null;
  name?: string;
  size?: 'sm' | 'md' | 'lg';
  showLevelBadge?: boolean;
  className?: string;
}

const sizeConfig = {
  sm: {
    container: 36,
    avatar: 28,
    ring: 36,
    stroke: 2.5,
    badge: 14,
    badgeIcon: 8,
  },
  md: {
    container: 44,
    avatar: 36,
    ring: 44,
    stroke: 3,
    badge: 18,
    badgeIcon: 10,
  },
  lg: {
    container: 60,
    avatar: 48,
    ring: 60,
    stroke: 3.5,
    badge: 22,
    badgeIcon: 12,
  },
};

export function GamifiedAvatar({
  src,
  name,
  size = 'md',
  showLevelBadge = false,
  className,
}: GamifiedAvatarProps) {
  const { data: myPoints } = useMyPoints();
  const { data: publicSettings } = usePublicGamificationSettings();

  const settingsData = publicSettings;
  const gamificationEnabled = settingsData?.enabled;

  const pointsData = myPoints;
  const totalPoints = pointsData?.totalPoints || 0;
  const currentLevel = pointsData?.level;
  const nextLevel = currentLevel?.nextLevel;

  const levelColor = currentLevel?.color || '#A3E635';

  const progressToNext = nextLevel
    ? Math.min(
        100,
        Math.max(
          0,
          Math.round(
            ((totalPoints - (currentLevel?.minPoints || 0)) /
              ((nextLevel.minPoints || nextLevel.pointsNeeded || 1) -
                (currentLevel?.minPoints || 0))) *
              100,
          ),
        ),
      )
    : 100;

  const cfg = sizeConfig[size];
  const avatarPx = cfg.avatar;
  const ringPx = cfg.container;
  const radius = (ringPx - cfg.stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - progressToNext / 100);

  // Fallback: no gamification or no data
  if (!gamificationEnabled || !pointsData) {
    return (
      <div className={cn('relative', className)}>
        <div className="avatar-ring rounded-full p-0.5">
          <Avatar
            className={cn('border-background border-2')}
            style={{ width: avatarPx, height: avatarPx }}
          >
            <AvatarImage src={src || undefined} alt={name} />
            <AvatarFallback className="bg-transparent">
              <PixelAvatar seed={name ?? ''} />
            </AvatarFallback>
          </Avatar>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn('relative', className)}
      style={{ width: ringPx, height: ringPx }}
      title={
        currentLevel?.name
          ? `${currentLevel.name} - ${totalPoints} XP`
          : undefined
      }
    >
      {/* SVG Ring */}
      <svg
        width={ringPx}
        height={ringPx}
        viewBox={`0 0 ${ringPx} ${ringPx}`}
        className="absolute inset-0"
        style={{ transform: 'rotate(-90deg)' }}
      >
        {/* Background track */}
        <circle
          cx={ringPx / 2}
          cy={ringPx / 2}
          r={radius}
          fill="none"
          stroke={levelColor}
          strokeOpacity={0.15}
          strokeWidth={cfg.stroke}
        />
        {/* Progress arc */}
        <circle
          cx={ringPx / 2}
          cy={ringPx / 2}
          r={radius}
          fill="none"
          stroke={levelColor}
          strokeWidth={cfg.stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          className="transition-all duration-700 ease-out"
        />
      </svg>

      {/* Avatar centered */}
      <div className="absolute inset-0 flex items-center justify-center">
        <Avatar style={{ width: avatarPx, height: avatarPx }}>
          <AvatarImage src={src || undefined} alt={name} />
          {/* Sem foto: o avatar gerado a partir do nome, nas cores da marca. */}
          <AvatarFallback className="bg-transparent">
            <PixelAvatar seed={name ?? ''} />
          </AvatarFallback>
        </Avatar>
      </div>

      {/* Level badge */}
      {showLevelBadge && currentLevel && (
        <div
          className="border-background absolute flex items-center justify-center rounded-full border-2 shadow-sm"
          style={{
            width: cfg.badge,
            height: cfg.badge,
            backgroundColor: levelColor,
            bottom: -1,
            right: -1,
          }}
        >
          <Star
            className="fill-white text-white"
            style={{ width: cfg.badgeIcon, height: cfg.badgeIcon }}
          />
        </div>
      )}
    </div>
  );
}
